import { useState, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { rendersService } from '@/shared/api/services/renders.service';
import { fetchImageBlob } from '@/shared/lib/utils/cors-image-loader';
import { useUpscaleProgress } from '@/features/design/album/hooks/use-upscale-progress';
import { useSubscriptionLimits } from '@/features/subscription/model/subscription.selectors';
import { toast } from '@/shared/lib/toast';

interface UseRenderDownloadOptions {
  renderId?: string | null;
  renderFileUrl?: string | null;
  isUpscaled?: boolean;
  /** Optional concept sequence number → used as filename prefix in the
   *  blob-fallback path (when the signed-URL API isn't reachable). The
   *  signed-URL path uses the server-supplied filename. */
  conceptSequenceNumber?: number;
}

interface UseRenderDownloadResult {
  // Download actions
  download: () => Promise<void>;
  download4K: () => Promise<void>;

  // Upscale state
  isUpscaling: boolean;
  upscaleProgress: number;
  hasUpscale: boolean;

  // Pending state (for auto-download)
  isPending4KDownload: boolean;
}

/**
 * Hook for downloading renders with optional 4K upscaling.
 * Handles:
 * - Standard download via signed URL
 * - Fallback download methods
 * - 4K upscale triggering
 * - Auto-download after upscale completes
 */
export function useRenderDownload({
  renderId,
  renderFileUrl,
  isUpscaled = false,
  conceptSequenceNumber,
}: UseRenderDownloadOptions): UseRenderDownloadResult {
  const [pendingDownload, setPendingDownload] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const tOptions = useTranslations('render.options');

  const limits = useSubscriptionLimits();
  const hasUpscale = limits?.has_upscale ?? false;

  const { upscaleRender, isUpscaling, getUpscaleProgress } =
    useUpscaleProgress();

  const isRenderUpscaling = renderId ? isUpscaling(renderId) : false;
  const upscaleProgress = renderId ? getUpscaleProgress(renderId) : 0;

  /**
   * Download render using signed URL from API with fallbacks
   */
  const download = useCallback(async () => {
    if (!renderId) return;

    try {
      // Get signed download URL from API
      const { url, filename } = await rendersService.getDownloadUrl(renderId);

      // Trigger browser download via hidden link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);

      // Fallback to direct file URL if API request fails
      if (renderFileUrl) {
        await downloadViaFetch(renderFileUrl, renderId, conceptSequenceNumber);
      }
    }
  }, [renderId, renderFileUrl, conceptSequenceNumber]);

  /**
   * Start 4K upscale and download when complete
   */
  const download4K = useCallback(async () => {
    if (!renderId) return;

    // If already upscaled, download immediately
    if (isUpscaled) {
      await download();
      return;
    }

    // If upscale is already in progress, don't start another one
    if (isRenderUpscaling) {
      return;
    }

    // Start upscale and mark for auto-download
    setPendingDownload(renderId);
    toast.info(tOptions('upscalePending'));

    try {
      await upscaleRender(renderId);
    } catch (_error) {
      // Error is already handled by useUpscale hook
      setPendingDownload(null);
    }
  }, [
    renderId,
    isUpscaled,
    isRenderUpscaling,
    download,
    upscaleRender,
    tOptions,
  ]);

  // Auto-download when upscale completes
  useEffect(() => {
    const shouldAutoDownload =
      pendingDownload &&
      pendingDownload === renderId &&
      isUpscaled &&
      !isRenderUpscaling &&
      !isDownloading;

    if (shouldAutoDownload) {
      setIsDownloading(true);
      // Browser surfaces its own "download complete" UI; no toast needed.
      download().finally(() => {
        setPendingDownload(null);
        setIsDownloading(false);
      });
    }
  }, [
    isUpscaled,
    isRenderUpscaling,
    pendingDownload,
    renderId,
    isDownloading,
    download,
  ]);

  return {
    download,
    download4K,
    isUpscaling: isRenderUpscaling,
    upscaleProgress,
    hasUpscale,
    isPending4KDownload: pendingDownload === renderId,
  };
}

/**
 * Fallback download via fetch + blob URL
 */
async function downloadViaFetch(
  fileUrl: string,
  renderId: string,
  conceptSequenceNumber?: number
): Promise<void> {
  try {
    // The page shows this URL via <img> — fetch through the cache-segregated
    // CORS loader, or Chrome serves the poisoned cache entry.
    const blob = await fetchImageBlob(fileUrl, 'download');
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const shortId = renderId.slice(-6);
    link.download = conceptSequenceNumber != null
      ? `concept-${conceptSequenceNumber}-${shortId}.jpg`
      : `render-${shortId}.jpg`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (fetchError) {
    console.error('Fallback download failed:', fetchError);
    // Last resort: open in new tab
    window.open(fileUrl, '_blank');
  }
}
